import {
  Checkbox,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import type { ReactNode } from "react";
import type { InstructorSessionByInstructorItemDTO } from "../api/client";

export type InstructorPayrollRowSelection = {
  selectedIds: Set<string>;
  onToggle: (instructorId: string, checked: boolean) => void;
  onSelectAll: (select: boolean) => void;
};

type InstructorSessionReportTableProps = {
  isLoading: boolean;
  data: InstructorSessionByInstructorItemDTO[];
  /** ຄໍາອະທິບາຍເພີ່ມໃຕ້ຕາຕະລາງ (ເຊັ່ນ ຄ່າຈ້າງຮອບເດືອນ) */
  footerNote?: string;
  renderRowActions?: (row: InstructorSessionByInstructorItemDTO) => ReactNode;
  /** ເມື່ອມີ — ສະແດງຄໍລຳແລກເພື່ອເລືອກຄູສຳລັບບັນທຶກຮອບຄ່າຈ້າງ */
  payrollSelection?: InstructorPayrollRowSelection | null;
};

function formatRateTrip(row: InstructorSessionByInstructorItemDTO): {
  line1: string;
  line2: string;
} {
  const fmt = (n: number, b: "per_hour" | "per_session"): string =>
    n > 0
      ? `${n.toLocaleString("lo-LA")}/${b === "per_session" ? "ຄັ້ງ" : "ຊມ."}`
      : "—";
  return {
    line1: `ທົ່ວໄປ ${fmt(row.generalClassRatePerHour, row.generalClassPayoutBasis)} · ສ່ວນຕົວ ${fmt(row.privateLessonRatePerHour, row.privateLessonPayoutBasis)}`,
    line2: `Partner ${fmt(row.partnerRatePerHour, row.partnerPayoutBasis)}`,
  };
}

function fmtHoursShort(n: number): string {
  if (n <= 0) return "—";
  return n.toLocaleString("lo-LA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatHoursBreakdown(row: InstructorSessionByInstructorItemDTO): {
  line1: string;
  line2: string;
} {
  return {
    line1: `ທົ່ວໄປ ${fmtHoursShort(row.billableHoursGeneralClass)} · ສ່ວນຕົວ ${fmtHoursShort(row.billableHoursPrivateLesson)}`,
    line2: `Partner ${fmtHoursShort(row.billableHoursPartner)}`,
  };
}

function formatKipPart(n: number): string {
  if (n <= 0) return "—";
  return n.toLocaleString("lo-LA");
}

function formatPayoutBreakdown(row: InstructorSessionByInstructorItemDTO): {
  line1: string;
  line2: string;
} {
  return {
    line1: `ທົ່ວໄປ ${formatKipPart(row.estimatedPayoutGeneralClass)} · ສ່ວນຕົວ ${formatKipPart(row.estimatedPayoutPrivateLesson)}`,
    line2: `Partner ${formatKipPart(row.estimatedPayoutPartner)}`,
  };
}

export function InstructorSessionReportTable({
  isLoading,
  data,
  footerNote,
  renderRowActions,
  payrollSelection,
}: InstructorSessionReportTableProps) {
  const hasPayroll = payrollSelection != null;
  const colSpan = 10 + (renderRowActions ? 1 : 0) + (hasPayroll ? 1 : 0);

  const allPayrollSelected =
    hasPayroll &&
    data.length > 0 &&
    data.every((r) => payrollSelection.selectedIds.has(r.instructorId));
  const somePayrollSelected =
    hasPayroll &&
    data.some((r) => payrollSelection.selectedIds.has(r.instructorId));
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {hasPayroll ? (
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      allPayrollSelected
                        ? true
                        : somePayrollSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(val) => {
                      payrollSelection.onSelectAll(val === true);
                    }}
                    aria-label="ເລືອກຄູທັງໝົດ"
                  />
                </TableHead>
              ) : null}
              <TableHead className="min-w-[160px]">ຄູສອນ</TableHead>
              <TableHead className="w-[72px] text-right">ລວມ</TableHead>
              <TableHead className="w-[72px] text-right">ຄລາດສ໌</TableHead>
              <TableHead className="w-[72px] text-right">ສ່ວນຕົວ</TableHead>
              <TableHead className="min-w-[96px] text-right">ສະຕູດິໂອ</TableHead>
              <TableHead className="min-w-[88px] text-right">
                ນອກສະຖານທີ່
              </TableHead>
              <TableHead className="w-[80px] text-right">Partner</TableHead>
              <TableHead className="min-w-[88px] text-right">
                ຊົ່ວໂມງ (ແຍກ kind)
              </TableHead>
              <TableHead className="min-w-[120px] text-right">
                ຈຳນວນເງິນ (ກີບ)
              </TableHead>
              <TableHead className="min-w-[120px] text-right">
                ຄ່າຈ້າງ (ແຍກ kind)
              </TableHead>
              {renderRowActions ? (
                <TableHead className="min-w-[100px] text-right">
                  ລາຍລະອຽດ
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="h-24 text-center text-muted-foreground"
                >
                  ກຳລັງໂຫລດ...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="h-24 text-center text-muted-foreground"
                >
                  ບໍ່ພົບຂໍ້ມູນໃນຊ່ວງວັນທີ່ເລືອກ
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const rates = formatRateTrip(row);
                const hoursBr = formatHoursBreakdown(row);
                const payoutBr = formatPayoutBreakdown(row);
                return (
                  <TableRow key={row.instructorId}>
                    {hasPayroll ? (
                      <TableCell className="w-10 align-middle">
                        <Label
                          htmlFor={`payroll-row-${row.instructorId}`}
                          className="sr-only"
                        >
                          ເລືອກບັນທຶກ {row.instructorName}
                        </Label>
                        <Checkbox
                          id={`payroll-row-${row.instructorId}`}
                          checked={payrollSelection.selectedIds.has(
                            row.instructorId,
                          )}
                          onCheckedChange={(val) => {
                            payrollSelection.onToggle(
                              row.instructorId,
                              Boolean(val),
                            );
                          }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="font-medium">
                      {row.instructorName}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.sessionsTaught.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.generalClassSessions.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.privateLessonSessions.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.studioClassSessions.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.offSiteClassSessions.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.partnerInstructorOnlyClassSessions.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-xs tabular-nums leading-tight">
                      <div className="font-medium text-foreground">
                        {row.billableHoursTotal.toLocaleString("lo-LA", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                      <div>{hoursBr.line1}</div>
                      <div>{hoursBr.line2}</div>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-xs leading-tight">
                      <div>{rates.line1}</div>
                      <div>{rates.line2}</div>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-xs tabular-nums leading-tight">
                      <div className="font-medium text-foreground">
                        {row.estimatedPayout > 0
                          ? `${row.estimatedPayout.toLocaleString("lo-LA")} ກີບ`
                          : "—"}
                      </div>
                      <div>{payoutBr.line1}</div>
                      <div>{payoutBr.line2}</div>
                    </TableCell>
                    {renderRowActions ? (
                      <TableCell className="text-right">
                        {renderRowActions(row)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {footerNote ? (
        <p className="text-muted-foreground text-xs leading-relaxed">
          {footerNote}
        </p>
      ) : null}
    </div>
  );
}
